import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Home as HomeIcon, Users, Plus, ShieldCheck, LogOut, Settings, Menu,
  Activity as ActivityIcon, Ticket, BookOpen,
} from "lucide-react";
import { useState } from "react";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader,
} from "@/components/ui/sheet";
import ViewAsBanner from "@/components/ViewAsBanner";

const Brand = () => (
  <Link to="/" data-testid="brand-home" className="flex items-center gap-2.5">
    <img src="/logo.png" alt="Team Smith Hub" className="w-9 h-9 rounded-md shadow-sm" />
    <div className="leading-tight">
      <div className="font-serif text-[#0A1F44] text-base font-semibold tracking-wide">Team Smith Hub</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961] font-semibold">Connect · Build · Grow · Lead</div>
    </div>
  </Link>
);

export default function Layout() {
  const { user, logout, isViewAs, pendingCount: pending = 0 } = useAuth();
  const [open, setOpen] = useState(false);
  // "Admin" nav reflects the EFFECTIVE role (subject role when in View-As).
  const isAdmin = user?.role === "admin";
  const isOrgAdmin = user?.role === "platinum" && !!user?.platinum_admin;
  const extra = [...(isAdmin ? [{ to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-admin" }] : []),
    ...(isOrgAdmin ? [{ to: "/org-admin", label: "My Organisation", icon: ShieldCheck, id: "nav-org-admin" }] : [])];

  const isOversight = user?.role === "oversight";
  const NAV = [
    { to: "/", label: "Home", icon: HomeIcon, end: true, id: "nav-home" },
    { to: "/pbos", label: "PBOs", icon: Users, id: "nav-pbos" },
    { to: "/activity", label: "Activity", icon: ActivityIcon, id: "nav-activity" },
    { to: "/conferences", label: "Conferences", icon: Ticket, id: "nav-conferences" },
    { to: "/hub", label: "Resources", icon: BookOpen, id: "nav-hub" },
    ...(isViewAs || isOversight ? [] : [{ to: "/pbos/new", label: "Add", icon: Plus, id: "nav-add" }]),
  ];

  return (
    <div className="min-h-screen bg-[#F7F5EF] text-slate-900 font-sans">
      {/* iOS: keep header content below the status bar / notch / Dynamic Island (viewport-fit=cover). */}
      <header className="app-header sticky top-0 z-30 bg-white border-b border-slate-200" data-testid="app-header">
        <div className="max-w-6xl mx-auto h-14 flex items-center justify-between pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
          <Brand />
          <div className="flex items-center gap-2">
            <div className="hidden lg:flex items-center gap-1 mr-2">
              {NAV.map((n) => (
                <NavLink key={n.id} to={n.to} end={n.end} data-testid={`${n.id}-desktop`}
                  className={({ isActive }) => `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                  {n.label}
                </NavLink>
              ))}
              {extra.map((n) => (
                <NavLink key={n.id} to={n.to} data-testid={`${n.id}-desktop`}
                  className={({ isActive }) => `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                  {n.label}{pending > 0 && <span className="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-[#8A1F1F] text-white text-[10px]" data-testid={`${n.id}-pending-badge`}>{pending}</span>}
                </NavLink>
              ))}
              <NavLink to="/settings" data-testid="nav-settings-desktop"
                className={({ isActive }) => `px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-slate-100"}`}>Settings</NavLink>
            </div>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="open-menu-btn" className="inline-flex items-center justify-center w-11 h-11 -mr-1 rounded-md hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961] lg:hidden" aria-label="Menu">
                  <Menu className="w-6 h-6 text-slate-700" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 pt-[max(env(safe-area-inset-top),1.5rem)] pb-[max(env(safe-area-inset-bottom),1.5rem)] overflow-y-auto">
                <SheetHeader><SheetTitle className="font-serif text-[#0A1F44]">Menu</SheetTitle></SheetHeader>
                <div className="mt-4 space-y-1">
                  <div className="px-2 py-2 rounded-md bg-slate-50 text-sm">
                    <div className="font-medium text-slate-900">{user?.name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-[#C9A961] font-semibold">
                      {user?.role}{user?.upline_team ? ` · ${user.upline_team}` : ""}
                    </div>
                  </div>
                  {[...NAV, ...extra, { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" }].map((n) => (
                    <NavLink key={n.id} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`${n.id}-mobile`}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                      <n.icon className="w-4 h-4" /> {n.label}
                      {pending > 0 && extra.some((x) => x.id === n.id) && <span className="ml-auto rounded-full bg-[#8A1F1F] text-white text-[10px] px-1.5">{pending}</span>}
                    </NavLink>
                  ))}
                  <button data-testid="logout-btn-mobile" onClick={() => { setOpen(false); logout(); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100">
                    <LogOut className="w-4 h-4" /> Log out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <button data-testid="logout-btn-desktop" onClick={logout} className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-slate-700 hover:bg-slate-100" title="Log out">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <ViewAsBanner />

      <main className="max-w-6xl mx-auto px-4 pb-24 pt-6 sm:pt-8">
        <Outlet />
        <footer className="mt-12 pt-4 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="app-footer">
          <span>Team Smith Hub</span>
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#0A1F44] hover:underline">Privacy Notice</Link>
        </footer>
      </main>

      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="max-w-6xl mx-auto grid grid-cols-5">
          <TabBtn to="/" end icon={HomeIcon} label="Home" testId="tab-home" />
          <TabBtn to="/pbos" icon={Users} label="PBOs" testId="tab-pbos" />
          <TabBtn to="/activity" icon={ActivityIcon} label="Activity" testId="tab-activity" />
          <TabBtn to="/conferences" icon={Ticket} label="Confs" testId="tab-confs" />
          <TabBtn to="/hub" icon={BookOpen} label="Hub" testId="tab-hub" />
        </div>
      </nav>
    </div>
  );
}

function TabBtn({ to, end, icon: Icon, label, testId, primary }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => `flex flex-col items-center justify-center py-2 gap-0.5 text-[11px] ${isActive ? "text-[#0A1F44]" : "text-slate-500"}`}>
      <span className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${primary ? "bg-[#0A1F44] text-[#C9A961]" : ""}`}>
        <Icon className={`w-5 h-5 ${primary ? "" : "text-current"}`} />
      </span>
      <span className="font-medium">{label}</span>
    </NavLink>
  );
}
